import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { BusyIndicator, ImageFacet, FacetVisibility, ControllerButton, ConnectionLines, AudioSourceFacet, LightSource, RenderableFacet, MulticastDelegate1, MulticastDelegate2, CollisionFacet, VisibilityFacet } from "flavr-common";
import { isRenderableType, RenderableUpdateTriggerState } from "./Renderable";
import { ModelTriggerState } from "../renderer/RendererModule";
import { PrimitiveMeshRenderable } from "../primitives/PrimitiveMeshRenderable";
import { MeshRenderable } from "../mesh/MeshRenderable";
import { Connections } from "../connections/connections";
import { BusyIndicatorRenderable } from "../busyIndicator/BusyIndicatorRenderable";
import { TextRenderable } from "../primitives/TextRenderable";
import { ControllerRenderable } from "../controller/ControllerRenderable";
import { RendererFacetFactory } from "./RendererFacetFactory";
import { RenderableFactory } from "./RenderableFactory";
import { modelPool } from "@modules/model";
export var RoomChangeType;
(function (RoomChangeType) {
    RoomChangeType[RoomChangeType["OUTSIDE_OF_CURRENT"] = 0] = "OUTSIDE_OF_CURRENT";
    RoomChangeType[RoomChangeType["IN_CURRENT"] = 1] = "IN_CURRENT";
    RoomChangeType[RoomChangeType["IN_DESCENDANT_OF_CURRENT"] = 2] = "IN_DESCENDANT_OF_CURRENT";
})(RoomChangeType || (RoomChangeType = {}));
/**
 * RendererModels are used for the internal scene representation of the RenderModule.
 * They contain all information that are necessary to render a model.
 * This information includes:
 *  - the current transformation of the model.
 *  - the current visibility of the model.
 *  - the Renderable that is used for rendering the RenderableFacet of the model (if it has one).
 *  - the FacetRenderers that are used to render additional facets (such as Annotation, Tooltips, ...) of the model.
 */
export class RendererModelInterface {
    constructor(modelUID, modelName, renderer, createdTime) {
        this.modelUID = modelUID;
        this.modelName = modelName;
        this.renderer = renderer;
        this.createdTime = createdTime;
        this.matrixWorld = new Matrix4();
        this.worldPosition = new Vector3();
        this.worldOrientation = new Quaternion();
        this.worldScale = new Vector3(1, 1, 1);
        this.invisibleFacets = new Set();
        // we only create anything after we received the first worldTransform
        this.matrixWorldValid = false;
        // if the model should be rendered at all
        this.visible = true;
        // parameter for clip fade of primitiveMesh
        this.clipFade = 0;
        this.facetRenderers = new Map();
        this.onRoomChangedDelegate = new MulticastDelegate1();
        this._isDestroyed = false;
    }
    get isDestroyed() {
        return this._isDestroyed;
    }
    destroy() {
        this._isDestroyed = true;
        if (this.renderable != null) {
            this.renderable.clearSubscriptions();
            this.renderable._destroy();
            this.renderable.destroy();
            this.renderable = undefined;
        }
        for (const it of this.facetRenderers.values()) {
            it.clearSubscriptions();
            it._destroy();
            it.destroy();
        }
        this.facetRenderers.clear();
        if (this.connections != null) {
            this.connections.clearSubscriptions();
            this.connections._destroy();
            this.connections.destroy();
            this.connections = undefined;
        }
        this.onRoomChangedDelegate.clear();
    }
    subscribeOtherRenderable(uid, type, callback) {
        this.renderer.subscribeModel(this.modelUID, uid, (newModel, modelState) => {
            switch (modelState) {
                case ModelTriggerState.Created:
                    newModel.subscribeRenderable(type, callback);
                    break;
                case ModelTriggerState.Destroyed:
                    newModel.unsubscribeRenderable(type, callback);
                    break;
            }
        });
    }
    unsubscribeOtherRenderable(uid, type, callback) {
        this.renderer.unsubscribeModel(uid, this.modelUID);
        const model = this.renderer.getModel(uid);
        if (model != null) {
            model.unsubscribeRenderable(type, callback);
        }
    }
    subscribeRenderable(type, callback) {
        if (this.renderableChanged === undefined) {
            this.renderableChanged = new Map();
        }
        const facetType = type.facetType;
        const facetTypeName = facetType.typestatic;
        let delegate = this.renderableChanged.get(facetTypeName);
        if (delegate === undefined) {
            delegate = new MulticastDelegate2();
            this.renderableChanged.set(facetTypeName, delegate);
        }
        delegate.addSafe(callback);
        if (type.type !== undefined) {
            if (this.renderable !== undefined && this.renderable.facetType === facetTypeName) {
                callback(RenderableUpdateTriggerState.Created, this.renderable);
                if (this.matrixWorldValid) {
                    callback(RenderableUpdateTriggerState.Init, this.renderable);
                }
            }
        }
        else {
            const renderer = this.facetRenderers.get(facetTypeName);
            if (renderer != null) {
                callback(RenderableUpdateTriggerState.Created, renderer);
                if (this.matrixWorldValid) {
                    callback(RenderableUpdateTriggerState.Init, renderer);
                }
            }
        }
    }
    unsubscribeRenderable(type, callback) {
        if (this.renderableChanged === undefined)
            return;
        const facetType = type.facetType;
        const facetTypeName = facetType.typestatic;
        const delegate = this.renderableChanged.get(facetTypeName);
        if (delegate === undefined)
            return;
        delegate.remove(callback);
    }
    triggerRenderableUpdate(renderable, state) {
        if (this.renderable != null && renderable !== this.renderable) {
            this.renderable.onOtherRenderableChanged(state, renderable);
        }
        for (const facetRenderer of this.facetRenderers.values()) {
            if (renderable === facetRenderer) {
                continue;
            }
            facetRenderer.onOtherRenderableChanged(state, renderable);
        }
        if (this.renderableChanged === undefined)
            return false;
        const delegate = this.renderableChanged.get(renderable.facetType);
        if (delegate === undefined)
            return false;
        delegate.invoke(state, renderable);
        return true;
    }
    getRenderableByType(type) {
        if (this.renderable == null)
            return undefined;
        if (!isRenderableType(type, this.renderable))
            return undefined;
        return this.renderable;
    }
    getFacetRenderer(type) {
        if (type.facetType === undefined) {
            // type.facetType is only defined if we have created
            // at least one instance of the FacetRenderer
            return undefined;
        }
        return this.facetRenderers.get(type.facetType.typestatic);
    }
    /**
     * Adds an object under this model's room root.
     * Use this for room-bound visuals that should follow room visibility and be hidden with their room.
     */
    addObjectToRoom(object) {
        this.renderer.addObjectToRoom(object, this.modelUID);
    }
    /**
     * Adds an object under the renderer's persistent root.
     * Use this for visuals that should remain visible across room transitions instead of being tied to a room root.
     */
    addObjectToPersistentRoot(object) {
        this.renderer.addObjectToPersistentRoot(object);
    }
    addObjectToScene(object) {
        this.addObjectToRoom(object);
    }
    childModelAdded(child) {
        if (this.renderable != null && this.renderable.addChildModel != null) {
            this.renderable.addChildModel(child);
        }
        const parent = this.getParentRenderModelInterface(this.modelUID);
        if (parent != null) {
            parent.childModelAdded(child);
        }
    }
    removeObjectFromRoom(object) {
        const parent = this.getParentRenderModelInterface(this.modelUID);
        if (parent != null && parent.renderable != null && parent.renderable.removeChildModel != null) {
            parent.renderable.removeChildModel(this);
        }
        this.renderer.removeObjectFromRoom(object, this.modelUID);
    }
    removeObjectFromPersistentRoot(object) {
        const parent = this.getParentRenderModelInterface(this.modelUID);
        if (parent != null && parent.renderable != null && parent.renderable.removeChildModel != null) {
            parent.renderable.removeChildModel(this);
        }
        this.renderer.removeObjectFromPersistentRoot(object);
    }
    removeObjectFromScene(object) {
        this.removeObjectFromRoom(object);
    }
    attachRoom(roomUid, room, transform) {
        if (this.attachedRooms == null) {
            this.attachedRooms = new Map();
        }
        this.attachedRooms.set(roomUid, {
            node: room,
            transform
        });
    }
    detachRoom(roomUid) {
        if (this.attachedRooms == null)
            return;
        this.attachedRooms.delete(roomUid);
    }
    isModelInCurrentRoom() {
        return this.renderer.isModelInCurrentRoom(this.modelUID);
    }
    isFacetVisible(facetType) {
        return this.visible && !this.invisibleFacets.has(facetType);
    }
    isVisible() {
        return this.visible;
    }
    changeFacetVisible(facetType, visible) {
        if (facetType === ConnectionLines.typestatic) {
            if (this.connections != null) {
                this.connections.setVisible(visible && this.isFacetVisible(facetType));
            }
            return;
        }
        const facet = this.facetRenderers.get(facetType);
        if (facet != null) {
            facet.setVisible(visible && this.isFacetVisible(facetType));
        }
    }
    onRoomChanged(type) {
        this.changeFacetVisible(AudioSourceFacet.typestatic, type === RoomChangeType.IN_CURRENT);
        this.changeFacetVisible(LightSource.typestatic, type === RoomChangeType.IN_CURRENT);
        const isNotOutside = type !== RoomChangeType.OUTSIDE_OF_CURRENT;
        this.changeFacetVisible(ConnectionLines.typestatic, isNotOutside);
        this.changeFacetVisible(ImageFacet.typestatic, isNotOutside);
        if (this.renderable != null) {
            // For PrimitiveMeshes and Texts we update the batches instead of the instances
            if (!isRenderableType(PrimitiveMeshRenderable, this.renderable) && !isRenderableType(TextRenderable, this.renderable)) {
                if (this.renderable.onRoomChanged != null) {
                    this.renderable.onRoomChanged(type);
                }
                else {
                    this.renderable.setVisible(isNotOutside && this.visible);
                }
            }
        }
        this.onRoomChangedDelegate.invoke(type);
    }
    setVisible(visible) {
        var _a, _b;
        if (this.visible === visible)
            return;
        this.visible = visible;
        if (!this.matrixWorldValid)
            return;
        (_a = this.renderable) === null || _a === void 0 ? void 0 : _a.setVisible(this.visible);
        (_b = this.connections) === null || _b === void 0 ? void 0 : _b.setVisible(this.visible);
        for (const it of this.facetRenderers.values()) {
            if (this.invisibleFacets.has(it.facetType))
                continue;
            it.setVisible(this.visible);
        }
    }
    setFacetVisible(facetType, facetsVisible) {
        if (facetsVisible) {
            this.invisibleFacets.delete(facetType);
        }
        else {
            this.invisibleFacets.add(facetType);
        }
        if (this.visible) {
            const facet = this.facetRenderers.get(facetType);
            facet != null && facet.setVisible(facetsVisible);
            if (this.connections != null && facetType === ConnectionLines.typestatic) {
                this.connections.setVisible(facetsVisible);
            }
        }
    }
    setClipFade(target) {
        this.clipFade = target;
        if (this.renderable == null)
            return;
        if (!isRenderableType(PrimitiveMeshRenderable, this.renderable))
            return;
        this.renderable.setClipFade(target);
    }
    setSubMeshTransform(subMeshName, position, orientation, scale) {
        const renderable = this.getRenderableByType(MeshRenderable);
        if (renderable == null)
            return;
        renderable.meshInstance.setSubMeshTransform(subMeshName, position, orientation, scale);
    }
    onControllerButtonUpdate(button) {
        const renderable = this.getRenderableByType(ControllerRenderable);
        if (renderable == null)
            return;
        renderable.onButtonUpdate(button);
    }
    getCenter(center) {
        if (this.renderable != null) {
            return this.renderable.getCenter(center);
        }
        return center.copy(this.worldPosition);
    }
    updateFacet(facet, timeStamp) {
        if (FacetVisibility.is(facet)) {
            this.setFacetVisible(facet.facetType, facet.visible);
        }
        else if (VisibilityFacet.is(facet)) {
            this.setVisible(facet.visible);
        }
        else if (ControllerButton.is(facet)) {
            this.onControllerButtonUpdate(facet);
        }
        else if (BusyIndicator.is(facet)) {
            let renderable = this.facetRenderers.get(BusyIndicator.typestatic);
            if (renderable == null) {
                renderable = new BusyIndicatorRenderable(this);
                renderable.facet = facet;
                this.facetRenderers.set(BusyIndicator.typestatic, renderable);
            }
            renderable.currentFacet = facet;
            renderable.updateFacet(facet);
            renderable.updateTransform();
        }
        else if (ConnectionLines.is(facet)) {
            let isCreated = false;
            if (this.connections == null) {
                this.connections = new Connections(this);
                isCreated = true;
            }
            if (this.matrixWorldValid) {
                if (isCreated) {
                    this.connections.onInit(facet);
                }
                this.connections.updateFacet(facet);
            }
            this.connections.facet = facet;
            this.connections.currentFacet = facet;
            this.connections.updatedTime = timeStamp;
            if (this.matrixWorldValid && isCreated) {
                this.connections.updateTransform();
                this.connections.setVisible(this.visible);
            }
        }
        else if (CollisionFacet.is(facet)) {
            this.collisionShape = facet.shape;
        }
        else if (RenderableFacet.is(facet)) {
            // Ignore renderables that were not registered
            if (!RenderableFactory.hasByInstance(facet))
                return;
            this.createOrUpdateRenderable(facet, timeStamp);
        }
        else {
            this.createOrUpdateFacetRenderer(facet, timeStamp);
        }
    }
    createOrUpdateRenderable(facet, timeStamp) {
        if (this.renderable != null && this.renderable.facetType !== facet.type) {
            this.renderable.clearSubscriptions();
            this.renderable.destroy();
            this.renderable = undefined;
        }
        let isCreated = false;
        if (this.renderable == null) {
            this.renderable = RenderableFactory.create(facet, this);
            isCreated = true;
        }
        if (this.matrixWorldValid) {
            const oldFacet = this.renderable.facet;
            this.renderable.currentFacet = facet;
            if (isCreated) {
                this.renderable.onInit(facet);
            }
            this.renderable.updateFacet(facet, oldFacet);
        }
        this.renderable.facet = facet;
        this.renderable.updatedTime = timeStamp;
        if (this.matrixWorldValid && isCreated) {
            this.renderable.updateTransform();
            this.renderable.setVisible(this.visible);
        }
        if (this.matrixWorldValid) {
            this.triggerRenderableUpdate(this.renderable, isCreated ? RenderableUpdateTriggerState.Created : RenderableUpdateTriggerState.Updated);
        }
    }
    createOrUpdateFacetRenderer(facet, timeStamp) {
        let isCreated = false;
        let renderer = this.facetRenderers.get(facet.type);
        if (renderer == null) {
            renderer = RendererFacetFactory.create(facet.type, this);
            if (renderer == null) {
                // the facet is not a renderabel facet so we will ignore it
                return;
            }
            this.facetRenderers.set(facet.type, renderer);
            isCreated = true;
        }
        if (this.matrixWorldValid) {
            const oldFacet = renderer.facet;
            renderer.currentFacet = facet;
            if (isCreated) {
                renderer.onInit(facet);
            }
            renderer.updateFacet(facet, oldFacet);
        }
        renderer.facet = facet;
        renderer.updatedTime = timeStamp;
        if (isCreated) {
            this.triggerRenderableUpdate(renderer, RenderableUpdateTriggerState.Created);
        }
        if (this.matrixWorldValid) {
            if (isCreated) {
                renderer.updateTransform();
                renderer.setVisible(this.isFacetVisible(facet.type));
            }
            if (!isCreated) {
                this.triggerRenderableUpdate(renderer, RenderableUpdateTriggerState.Updated);
            }
        }
    }
    removeFacet(facetType, timeStamp) {
        if (RenderableFacet.isDerivedType(facetType)) {
            if (this.renderable == null)
                return;
            if (this.renderable.updatedTime >= timeStamp)
                return;
            this.renderable.clearSubscriptions();
            this.renderable._destroy();
            this.renderable.destroy();
            this.renderable = undefined;
        }
        else {
            const facet = this.facetRenderers.get(facetType);
            if (facet == null)
                return;
            if (facet.updatedTime >= timeStamp)
                return;
            facet.clearSubscriptions();
            facet._destroy();
            facet.destroy();
            this.facetRenderers.delete(facetType);
        }
    }
    removeFacetsUntil(timeStamp) {
        if (this.renderable != null) {
            if (this.renderable.updatedTime < timeStamp) {
                this.renderable.clearSubscriptions();
                this.renderable._destroy();
                this.renderable.destroy();
                this.renderable = undefined;
            }
        }
        for (const [type, facet] of this.facetRenderers) {
            if (facet.updatedTime < timeStamp) {
                facet.clearSubscriptions();
                facet._destroy();
                facet.destroy();
                this.facetRenderers.delete(type);
            }
        }
    }
    getWorldTransform(position, orientation, scale) {
        position.copy(this.worldPosition);
        orientation.copy(this.worldOrientation);
        scale.copy(this.worldScale);
    }
    updateTransform(transformation, boundingBoxMin, boundingBoxMax, resultOfTimeStamp) {
        var _a, _b;
        this.matrixWorld.copy(transformation);
        this.matrixWorld.decompose(this.worldPosition, this.worldOrientation, this.worldScale);
        if (!this.matrixWorldValid) {
            this.matrixWorldValid = true;
            this.initializeRenderables();
        }
        else {
            (_a = this.renderable) === null || _a === void 0 ? void 0 : _a.updateTransform(undefined, undefined, resultOfTimeStamp);
            for (const it of this.facetRenderers.values()) {
                it.updateTransform(boundingBoxMin, boundingBoxMax, resultOfTimeStamp);
            }
            (_b = this.connections) === null || _b === void 0 ? void 0 : _b.updateTransform();
        }
        if (this.attachedRooms != null) {
            for (const room of this.attachedRooms.values()) {
                const node = room.node;
                node.matrix.multiplyMatrices(this.matrixWorld, room.transform);
                node.updateMatrixWorld(true);
            }
        }
        const transEvents = this.renderer.transformEvents.get(this.modelUID);
        if (transEvents != null) {
            for (const [, transformCallback] of transEvents) {
                transformCallback(this);
            }
        }
        this.renderer.selectionHighlightMgr.handleModelTransformationUpdate(this.modelUID, this);
    }
    /// Private helper methods ///
    initializeRenderables() {
        // this should be the first call to updateFacet
        if (this.renderable != null) {
            this.initializeRenderable(this.renderable);
            this.renderable.updateTransform();
            this.renderable.setVisible(this.visible);
        }
        if (this.connections != null) {
            if (this.connections.facet != null) {
                this.connections.onInit(this.connections.facet);
                this.connections.updateFacet(this.connections.facet);
            }
            this.connections.updateTransform();
            this.connections.setVisible(this.visible);
        }
        for (const it of this.facetRenderers.values()) {
            this.initializeRenderable(it);
            it.updateTransform();
            it.setVisible(this.isFacetVisible(it.facetType));
        }
        const parent = modelPool.getModelOrThrow(this.modelUID).getParent();
        if (parent === undefined)
            return;
        const parentInterface = this.renderer.getModel(parent.getUID());
        if (parentInterface != null) {
            parentInterface.childModelAdded(this);
        }
    }
    initializeRenderable(renderable) {
        if (renderable.facet === undefined) {
            throw new Error(`facet are not set in renderable with facetType ${renderable.facetType}`);
        }
        const newFacet = renderable.facet;
        renderable.facet = undefined;
        renderable.currentFacet = newFacet;
        renderable.onInit(newFacet);
        renderable.updateFacet(newFacet, undefined);
        this.triggerRenderableUpdate(renderable, RenderableUpdateTriggerState.Init);
        renderable.facet = newFacet;
    }
    getParentRenderModelInterface(uid) {
        var _a;
        const parent = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getParent();
        if (parent === undefined)
            return;
        return this.renderer.getModel(parent.getUID());
    }
}
